/**
 * EffectivePermissionsService — the aggregation seam for "what can this subject actually do, and why".
 *
 * The Access workspace's Effective visualization + (later) the permission-expression layer depend ONLY on
 * this service's interface, never on the raw Permissions/* endpoints. Today it COMPOSES the existing
 * endpoints client-side (there is no single "all contexts x all permissions" endpoint); when a server-side
 * `Permissions/Matrix/For/User` aggregation lands, its internals swap for that one call and no view changes.
 *
 * Interface (mirrors the future endpoint's payload):
 *   resolveUserMatrixAsync(pIDUser, pOptions) -> {
 *     User, Contexts:[{ Context, IDContext, Label, MembershipVia:[{IDRole,RoleName,MembershipType}], Roles, IsSuperUser }],
 *     Permissions:{ '<Context>:<IDContext>': { '<hash>': { Allowed, Source, IDRole, RoleName, GrantContext } } } }
 *   resolveRoleMatrixAsync(pIDRole, pContext, pIDContext, pRoleRecord) -> single-context variant (+ Summary)
 *   explainAsync(pIDUser, pHash, pContext, pIDContext) -> the Permissions/Explain trace (pass-through)
 *   compareAsync(...) / canAsync(...) -> secondary lenses over the same data
 *
 * Registered as a fable service so any provider resolves it via `this.fable.EffectivePermissionsService`.
 * Exercised only at runtime in the browser against a live API — verify end-to-end on QA.
 */

const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libCatalog = require('../access/Permission-Catalog.js');

// Reads the home app always shows even without an explicit grant — the server row-filters them instead of
// gating the control (matches app_headlight PermissionService / bootstraps Permissions.js). Mirrored here so
// the expression layer doesn't over-hide list/nav entries the server would actually populate.
const ALWAYS_ALLOW = { 'Document-Read': true, 'Project-Read': true, 'Contract-Read': true };

class EffectivePermissionsService extends libFableServiceProviderBase
{
	/**
	 * @param {*} pFable - The fable instance.
	 * @param {*} [pOptions] - Provider options.
	 * @param {string} [pServiceHash] - Fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict = this.fable;
		this.serviceType = 'EffectivePermissionsService';

		// Phase 2 — the CURRENT user's resolved capability map, cached for synchronous UI gating (create
		// buttons, nav). Populated by loadCurrentUserAsync() at login. See can()/canModule().
		this._currentUserCap = null;
	}

	get restClient()
	{
		return this.fable.HeadlightRestClient || (this.fable.pict && this.fable.pict.HeadlightRestClient);
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Read an entire filtered set (Count then a single sized page).
	 * @return {Promise<Array>}
	 */
	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 5000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// --- Public interface -----------------------------------------------------

	/**
	 * The full effective-permission matrix for a user: every context they hold a role in (Global + each
	 * project / contract / lab), and per context every explicitly resolved permission with its provenance.
	 * @param {Number} pIDUser
	 * @param {Object} [pOptions] - { Module? } (Module scopes the per-context Map to one module)
	 * @return {Promise<Object>}
	 */
	async resolveUserMatrixAsync(pIDUser, pOptions)
	{
		const tmpOptions = pOptions || {};
		// 1. The contexts where the user holds roles, with membership attribution.
		let tmpContextsResult = null;
		try
		{
			tmpContextsResult = await this._getJSON(`Permissions/Roles/For/User/${pIDUser}/All/Contexts`);
		}
		catch (pError)
		{
			tmpContextsResult = null;
		}
		const tmpContexts = this._normalizeContexts(tmpContextsResult);
		// 2. Per context, resolve the full permission map (all modules unless one is named). Run the contexts
		// CONCURRENTLY in bounded batches instead of one sequential round-trip each — a long-tenured user on a large
		// customer holds roles in hundreds of contexts, and serial awaits made the Effective tab wait for hundreds of
		// sequential requests before it could render. Batching keeps it fast without flooding the server.
		const tmpPermissions = {};
		const tmpConcurrency = 8;
		for (let tmpStart = 0; tmpStart < tmpContexts.length; tmpStart += tmpConcurrency)
		{
			const tmpBatch = tmpContexts.slice(tmpStart, tmpStart + tmpConcurrency);
			/* eslint-disable no-await-in-loop */
			await Promise.all(tmpBatch.map((pCtx) => this._mapForContextAsync(pIDUser, pCtx.Context, pCtx.IDContext, tmpOptions).then((pMap) =>
			{
				tmpPermissions[`${pCtx.Context}:${pCtx.IDContext}`] = pMap.perms;
				pCtx.Roles = pMap.roles;
				pCtx.IsSuperUser = pMap.isSuperUser;
			})));
			/* eslint-enable no-await-in-loop */
		}
		return {
			User: (tmpContextsResult && tmpContextsResult.User) || null,
			Contexts: tmpContexts,
			Permissions: tmpPermissions,
		};
	}

	/**
	 * The effective permissions for a role/group in a single context (its direct grants + those inherited
	 * from included permission groups), each tagged Direct vs Inherited.
	 * @param {Number} pIDRole
	 * @param {String} pContext
	 * @param {Number} pIDContext
	 * @param {Object} [pRoleRecord] - the Role row (for its IDCustomer → storage scope)
	 * @return {Promise<Object>}
	 */
	async resolveRoleMatrixAsync(pIDRole, pContext, pIDContext, pRoleRecord)
	{
		const tmpEffective = await this._getJSON(`Permissions/For/Role/${pIDRole}/In/${pContext}/${pIDContext}`);
		const tmpList = Array.isArray(tmpEffective) ? tmpEffective : [];
		// The role's OWN direct grants live in its storage scope: GLOBAL roles in the membership context
		// (CustomerMembership/0), CUSTOMER-SPECIFIC roles in the entity context (Customer/<IDCustomer>).
		const tmpRoleCustomer = Number((pRoleRecord && pRoleRecord.IDCustomer) || 0);
		let tmpDirectContext;
		let tmpDirectID;
		if (pContext === 'Customer')
		{
			tmpDirectContext = (tmpRoleCustomer === 0) ? 'CustomerMembership' : 'Customer';
			tmpDirectID = (tmpRoleCustomer === 0) ? 0 : tmpRoleCustomer;
		}
		else
		{
			tmpDirectContext = libCatalog.contextToMembership(pContext);
			tmpDirectID = Number(pIDContext);
		}
		const tmpDirectRows = await this._getFilteredAll('RolePermissions',
			`FBV~Context~EQ~${tmpDirectContext}~FBV~IDContext~EQ~${tmpDirectID}~FBV~IDRole~EQ~${pIDRole}~FBV~Allowed~EQ~1`, 5000);
		const tmpDirect = new Set(tmpDirectRows.map((pRow) => pRow.Hash));
		const tmpPerms = {};
		tmpList.forEach((pHash) =>
		{
			tmpPerms[pHash] = { Allowed: true, Source: tmpDirect.has(pHash) ? 'Direct' : 'Inherited', IDRole: pIDRole, RoleName: '', GrantContext: pContext };
		});
		const tmpDirectCount = tmpList.filter((pHash) => tmpDirect.has(pHash)).length;
		return {
			Contexts: [ { Context: pContext, IDContext: pIDContext, Label: this._contextLabel(pContext, pIDContext), MembershipVia: [], Roles: [], IsSuperUser: false } ],
			Permissions: { [`${pContext}:${pIDContext}`]: tmpPerms },
			Summary: { Total: tmpList.length, Direct: tmpDirectCount, Inherited: tmpList.length - tmpDirectCount },
		};
	}

	/**
	 * The full "why?" trace for one permission for a user in a context (pass-through to Permissions/Explain).
	 * @return {Promise<Object>}
	 */
	async explainAsync(pIDUser, pHash, pContext, pIDContext)
	{
		return this._getJSON(`Permissions/Explain/${pHash}/In/${pContext}/${pIDContext}/For/User/${pIDUser}`);
	}

	/**
	 * "Why here but not there" for one permission across two contexts (pass-through to Permissions/Compare).
	 * Secondary lens (the simulator) — shape is best-effort until wired to a live response.
	 * @return {Promise<Object>}
	 */
	async compareAsync(pIDUser, pHash, pContextA, pIDContextA, pContextB, pIDContextB)
	{
		return this._getJSON(`Permissions/Compare/${pHash}/In/${pContextA}/${pIDContextA}/To/${pContextB}/${pIDContextB}/For/User/${pIDUser}`);
	}

	/**
	 * Phase-2 convenience: is a user allowed an (entity, action) in a context? Resolves the context map and
	 * checks the resolved hash. (The expression layer will cache a whole matrix rather than call per-check.)
	 * @return {Promise<Boolean>}
	 */
	async canAsync(pIDUser, pEntity, pAction, pContext, pIDContext)
	{
		const tmpMap = await this._mapForContextAsync(pIDUser, pContext, pIDContext, {});
		const tmpEntry = tmpMap.perms[libCatalog.buildHash(pEntity, pAction)];
		return !!(tmpEntry && tmpEntry.Allowed);
	}

	// --- Phase 2: the current user's capability cache (the UI permission-expression layer) --------------
	// Verified against the authoritative Permissions/Check endpoint: the resolved Map is fail-CLOSED —
	// a hash ALLOW/DENY in the Map matches Check, and a hash ABSENT from the Map is denied by Check too
	// (even accounting for PermissionsEnforcement). So the sync gates below hide a control iff the Map says
	// the current user can't (super-users bypass; before the map loads, nothing is hidden).

	/**
	 * Resolve + cache the CURRENT user's permission map at their operating context (default: their Customer),
	 * so the UI can gate synchronously. Idempotent per (user, context) unless pForce.
	 * @param {Number} pIDUser
	 * @param {Number} pIDCustomer - the operating context id (the user's customer)
	 * @param {Boolean} [pForce]
	 * @return {Promise<Object>} the cached capability record
	 */
	async loadCurrentUserAsync(pIDUser, pIDCustomer, pForce)
	{
		const tmpID = Number(pIDUser);
		const tmpCustomer = Number(pIDCustomer) || 0;
		if (this._currentUserCap && !pForce && this._currentUserCap.IDUser === tmpID && this._currentUserCap.IDContext === tmpCustomer)
		{
			return this._currentUserCap;
		}
		let tmpMap = { perms: {}, roles: [], isSuperUser: false };
		try
		{
			tmpMap = await this._mapForContextAsync(tmpID, 'Customer', tmpCustomer, {});
		}
		catch (pError)
		{
			// leave the cache un-ready → gates fail open (show everything) rather than hide on a load error
			this._currentUserCap = { IDUser: tmpID, IDContext: tmpCustomer, Perms: {}, IsSuperUser: false, Ready: false };
			return this._currentUserCap;
		}
		// Super-user comes from the MAP (the server resolver's own decision — true for a real customer-1/role-5
		// super-admin). We deliberately do NOT treat a session SuperAdminAccessRole as super here: the server
		// still resolves per-permission for those users (e.g. it denies them modules/entities they lack), so
		// gating on the resolved map keeps the UI matching the server rather than over-showing.
		this._currentUserCap = { IDUser: tmpID, IDContext: tmpCustomer, Perms: tmpMap.perms, IsSuperUser: !!tmpMap.isSuperUser, Ready: true };
		return this._currentUserCap;
	}

	/** @return {Boolean} whether the current user's capability map has loaded. */
	currentUserReady()
	{
		return !!(this._currentUserCap && this._currentUserCap.Ready);
	}

	/**
	 * Can the current user perform (entity, action)? Synchronous, for UI gating. Super-users → true; an
	 * unloaded cache → true (never hide before we know); otherwise the Map's decision (absent → false).
	 * @return {Boolean}
	 */
	can(pEntity, pAction)
	{
		return this.canHash(libCatalog.buildHash(pEntity, pAction));
	}

	/**
	 * can() by raw hash (e.g. an association or a `DocumentType-<Hash>-View`).
	 * @return {Boolean}
	 */
	canHash(pHash)
	{
		const tmpCap = this._currentUserCap;
		if (!tmpCap || !tmpCap.Ready)
		{
			return true;
		}
		if (tmpCap.IsSuperUser || ALWAYS_ALLOW[pHash])
		{
			return true;
		}
		const tmpEntry = tmpCap.Perms[pHash];
		return !!(tmpEntry && tmpEntry.Allowed);
	}

	/**
	 * Resolve + cache the current user's permission map in one project, for pages about a record that sits in a
	 * project: a project role can grant there what the customer-level map doesn't. Idempotent per project; a
	 * failed read caches nothing, so the next page load tries again.
	 * @param {Number} pIDProject
	 * @return {Promise<void>}
	 */
	async loadCurrentUserProjectAsync(pIDProject)
	{
		const tmpCap = this._currentUserCap;
		const tmpIDProject = Number(pIDProject) || 0;
		if (!tmpCap || !tmpCap.Ready || !(tmpIDProject > 0))
		{
			return;
		}
		if (!tmpCap.ProjectPerms)
		{
			tmpCap.ProjectPerms = {};
		}
		if (tmpCap.ProjectPerms[tmpIDProject])
		{
			return;
		}
		const tmpMap = await this._mapForContextAsync(tmpCap.IDUser, 'Project', tmpIDProject, {});
		if (Object.keys(tmpMap.perms).length > 0)
		{
			tmpCap.ProjectPerms[tmpIDProject] = tmpMap.perms;
		}
	}

	/**
	 * canHash(), also counting what the current user is granted in one project (once loadCurrentUserProjectAsync
	 * has read it).
	 * @param {String} pHash
	 * @param {Number} pIDProject
	 * @return {Boolean}
	 */
	canHashInProject(pHash, pIDProject)
	{
		if (this.canHash(pHash))
		{
			return true;
		}
		const tmpProjectPerms = this._currentUserCap && this._currentUserCap.ProjectPerms;
		const tmpEntry = tmpProjectPerms && tmpProjectPerms[Number(pIDProject) || 0] && tmpProjectPerms[Number(pIDProject) || 0][pHash];
		return !!(tmpEntry && tmpEntry.Allowed);
	}

	/**
	 * Can the current user access a module (nav gating)? Accepts `LIMS`, `Module-LIMS`, or `Module-LIMS-Access`.
	 * @return {Boolean}
	 */
	canModule(pModule)
	{
		const tmpCap = this._currentUserCap;
		if (!tmpCap || !tmpCap.Ready)
		{
			return true;
		}
		if (tmpCap.IsSuperUser)
		{
			return true;
		}
		let tmpHash = String(pModule || '');
		if (tmpHash.indexOf('Module-') !== 0)
		{
			tmpHash = `Module-${tmpHash}`;
		}
		if (!/-Access$/.test(tmpHash))
		{
			tmpHash = `${tmpHash}-Access`;
		}
		const tmpEntry = tmpCap.Perms[tmpHash];
		return !!(tmpEntry && tmpEntry.Allowed);
	}

	// --- Internal composition (swap this half for the aggregation endpoint later) ---

	/**
	 * Resolve the permission map for one context: `Permissions/Map/In/<Ctx>/<CtxID>/For/User/<id>` (all
	 * modules) — each permission annotated with Source + the implying role. Best-effort (a failing context
	 * yields an empty map rather than aborting the whole matrix).
	 * @return {Promise<{ perms: Object, roles: Array, isSuperUser: Boolean }>}
	 */
	async _mapForContextAsync(pIDUser, pContext, pIDContext, pOptions)
	{
		const tmpModule = pOptions && pOptions.Module;
		const tmpURL = tmpModule
			? `Permissions/Map/In/${pContext}/${pIDContext}/By/Module/${tmpModule}/For/User/${pIDUser}`
			: `Permissions/Map/In/${pContext}/${pIDContext}/For/User/${pIDUser}`;
		try
		{
			const tmpMap = await this._getJSON(tmpURL);
			return this._normalizePermissions(tmpMap);
		}
		catch (pError)
		{
			return { perms: {}, roles: [], isSuperUser: false };
		}
	}

	/**
	 * Normalize the `Permissions/Roles/For/User/:id/All/Contexts` payload into an ordered, DE-DUPLICATED
	 * context list. The endpoint returns one row PER role — { Context, IDContext, IDRole, ProxyIDRole } — plus
	 * a top-level `Roles` lookup (the role records). So a user with several roles in one project appears as
	 * several rows for the same Context:IDContext; we group them and resolve each IDRole to its name via the
	 * lookup (ProxyIDRole>0 marks a delegated assignment).
	 * @return {Array<Object>}
	 */
	_normalizeContexts(pResult)
	{
		const tmpEntries = (pResult && Array.isArray(pResult.Contexts)) ? pResult.Contexts : [];
		const tmpRolesRaw = (pResult && Array.isArray(pResult.Roles)) ? pResult.Roles : [];
		const tmpRoleLookup = {};
		tmpRolesRaw.forEach((pRole) =>
		{
			if (pRole && (pRole.IDRole !== undefined))
			{
				tmpRoleLookup[pRole.IDRole] = pRole;
			}
		});

		const tmpByKey = {};
		const tmpOrder = [];
		tmpEntries.forEach((pEntry) =>
		{
			const tmpContext = pEntry.Context || pEntry.Scope || 'Customer';
			const tmpID = Number(pEntry.IDContext || pEntry.ContextID || 0);
			const tmpKey = `${tmpContext}:${tmpID}`;
			if (!tmpByKey[tmpKey])
			{
				tmpByKey[tmpKey] = { Context: tmpContext, IDContext: tmpID, Label: this._contextLabel(tmpContext, tmpID), MembershipVia: [], _seen: {} };
				tmpOrder.push(tmpKey);
			}
			const tmpGroup = tmpByKey[tmpKey];
			const tmpIDRole = Number(pEntry.IDRole || 0);
			if (tmpIDRole && !tmpGroup._seen[tmpIDRole])
			{
				tmpGroup._seen[tmpIDRole] = true;
				const tmpRole = tmpRoleLookup[tmpIDRole] || {};
				tmpGroup.MembershipVia.push(
					{
						IDRole: tmpIDRole,
						RoleName: tmpRole.Name || `Role ${tmpIDRole}`,
						MembershipType: (Number(pEntry.ProxyIDRole) > 0) ? 'delegated' : (tmpRole.Type || ''),
					});
			}
		});
		return tmpOrder.map((pKey) =>
		{
			const tmpGroup = tmpByKey[pKey];
			delete tmpGroup._seen;
			return tmpGroup;
		});
	}

	/**
	 * Normalize a `Permissions/Map` payload: { Permissions:{ hash:{Allowed,Source,IDRole,RoleName} }, Roles, IsSuperUser }.
	 * @return {{ perms: Object, roles: Array, isSuperUser: Boolean }}
	 */
	_normalizePermissions(pMap)
	{
		const tmpPermissions = (pMap && pMap.Permissions) || {};
		const tmpPerms = {};
		Object.keys(tmpPermissions).forEach((pHash) =>
		{
			const tmpEntry = tmpPermissions[pHash] || {};
			tmpPerms[pHash] =
			{
				Allowed: !!tmpEntry.Allowed,
				Source: tmpEntry.Source || 'Default',
				IDRole: Number(tmpEntry.IDRole || 0),
				RoleName: tmpEntry.RoleName || '',
				GrantContext: tmpEntry.GrantContext || tmpEntry.Context || '',
			};
		});
		return { perms: tmpPerms, roles: (pMap && pMap.Roles) || [], isSuperUser: !!(pMap && pMap.IsSuperUser) };
	}

	_contextLabel(pContext, pIDContext)
	{
		if (pContext === 'Customer')
		{
			return pIDContext ? `Customer ${pIDContext}` : 'Global';
		}
		return pIDContext ? `${pContext} ${pIDContext}` : pContext;
	}
}

module.exports = EffectivePermissionsService;
